import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
import { useController, useFormContext } from 'react-hook-form';
import { ChangeEvent, FC } from 'react';
import { useTheme } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { TNumericField } from './type';
import { useAppSelector } from '../../hooks/useAppStore';
import { getMembers } from '../../store/team/selectors';

const NumericField: FC<TNumericField> = ({
  name,
  onChange,
  disabled = false,
  size = 'small',
  placeholder = '',
  borderRadius = 8,
  maxLength = Infinity,
  allowRange = [0, Infinity],
  adornment = null,
}) => {
  const { control, setError, clearErrors } = useFormContext();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const members = useAppSelector(getMembers);

  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    defaultValue: '',
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    inputProps.onChange(e);

    if (onChange) {
      onChange(e.target.value);
    }

    const pincode = e.target.value;
    const isUnique = !members.some((member) => member.pinCode === pincode);

    if (!isUnique) {
      setError(name, {
        type: 'manual',
        message: 'This pincode is already taken by another member',
      });
    } else {
      clearErrors(name);
    }
  };

  return (
    <NumericFormat
      {...inputProps}
      id={name}
      disabled={disabled}
      allowNegative={false}
      autoFocus={!isMobile}
      placeholder={placeholder}
      fullWidth
      sx={{
        '.MuiOutlinedInput-root': {
          borderRadius,
        },
      }}
      inputRef={ref}
      onChange={handleChange}
      size={size}
      customInput={TextField}
      InputProps={{
        startAdornment: adornment && (
          <InputAdornment position="start">
            <Typography variant="body2">{adornment}</Typography>
          </InputAdornment>
        ),
      }}
      isAllowed={(values) => {
        const { floatValue, formattedValue } = values;
        return (
          (floatValue === undefined
            || (floatValue >= allowRange[0] && floatValue <= allowRange[1]))
          && formattedValue.length <= maxLength
        );
      }}
    />
  );
};

export { NumericField };
